import { Link, createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import PlusIcon from "lucide-react/dist/esm/icons/plus";
import MoreHorizontalIcon from "lucide-react/dist/esm/icons/more-horizontal";
import SettingsIcon from "lucide-react/dist/esm/icons/settings";
import RotateCwIcon from "lucide-react/dist/esm/icons/rotate-cw";
import ArchiveIcon from "lucide-react/dist/esm/icons/archive";
import Trash2Icon from "lucide-react/dist/esm/icons/trash-2";
import EditIcon from "lucide-react/dist/esm/icons/edit";
import { Button } from "@quake/shared-ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@quake/shared-ui/components/ui/dropdown-menu";
import { Input } from "@quake/shared-ui/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@quake/shared-ui/components/ui/select";
import { Switch } from "@quake/shared-ui/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@quake/shared-ui/components/ui/table";
import { toast } from "@quake/shared-ui/lib/sonner";
import { type ListSharingMode } from "@quake/isomorphic/lists";
import { useDebounce } from "@quake/shared-ui/hooks/useDebounce";
import { checkPermission } from "~/store/usePermissionStore";
import { trpc } from "~/utils/trpc";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import {
  ConfirmListActionDialog,
  ListFormDialog,
  ListStateBadge,
  ListTypesDialog,
  RenewListDialog,
  VisibilityBadge,
  canManageList,
  formatExpiryDate,
  formatDateTime,
  isListOwner,
  isListExpired,
  type ListFormPayload,
  type ListSummary,
} from "./-components/lists-ui";

export const Route = createFileRoute("/_dashboard/$datasetSlug/data/lists/")({
  component: ListsIndexRoute,
});

type VisibilityFilter = ListSharingMode | "all";

function ListsIndexRoute() {
  const { datasetSlug } = Route.useParams();
  useBreadCrumbs([{ title: "Lists", link: `/${datasetSlug}/data/lists` }]);
  return <ListsIndexPage datasetSlug={datasetSlug} />;
}

function ListsIndexPage({ datasetSlug }: { datasetSlug: string }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 250);
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>("all");
  const [listTypeFilter, setListTypeFilter] = useState("all");
  const [includeExpired, setIncludeExpired] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [typeManagerOpen, setTypeManagerOpen] = useState(false);
  const [editingList, setEditingList] = useState<ListSummary | null>(null);
  const [renewingList, setRenewingList] = useState<ListSummary | null>(null);
  const [archivingList, setArchivingList] = useState<ListSummary | null>(null);
  const [deletingList, setDeletingList] = useState<ListSummary | null>(null);

  const listTypesQuery = useQuery(trpc.lists.listTypes.queryOptions({ includeArchived: true }));
  const sessionQuery = useQuery(trpc.auth.getSession.queryOptions());
  const listsQuery = useQuery(
    trpc.lists.list.queryOptions(
      {
        includeArchived,
        includeExpired,
        search: debouncedSearch.trim() || undefined,
        sharingMode: visibilityFilter === "all" ? undefined : visibilityFilter,
        listTypeId: listTypeFilter === "all" ? undefined : listTypeFilter,
        page: 1,
        pageSize: 100,
      },
      { placeholderData: (previousData) => previousData },
    ),
  );

  const invalidateLists = async () => {
    await Promise.all([
      queryClient.invalidateQueries(trpc.lists.list.queryFilter()),
      queryClient.invalidateQueries(trpc.lists.listTypes.queryFilter()),
    ]);
  };

  const createList = useMutation(
    trpc.lists.create.mutationOptions({
      onSuccess: async () => {
        await invalidateLists();
        toast.success("List created.");
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  const updateList = useMutation(
    trpc.lists.update.mutationOptions({
      onSuccess: async () => {
        await invalidateLists();
        toast.success("List updated.");
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  const archiveList = useMutation(
    trpc.lists.archive.mutationOptions({
      onSuccess: async () => {
        await invalidateLists();
        toast.success("List archived.");
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  const renewList = useMutation(
    trpc.lists.renew.mutationOptions({
      onSuccess: async () => {
        await invalidateLists();
        toast.success("List renewed.");
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  const hardDeleteList = useMutation(
    trpc.lists.hardDelete.mutationOptions({
      onSuccess: async () => {
        await invalidateLists();
        toast.success("List deleted.");
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  const listTypes = listTypesQuery.data ?? [];
  const lists = useMemo(() => listsQuery.data ?? [], [listsQuery.data]);
  const totalMembers = useMemo(() => lists.reduce((total, list) => total + list.member_count, 0), [lists]);
  const expiredCount = useMemo(() => lists.filter((list) => isListExpired(list)).length, [lists]);

  const submitCreate = async (payload: ListFormPayload) => {
    await createList.mutateAsync({
      listTypeId: payload.listTypeId,
      name: payload.name,
      description: payload.description,
      context: payload.context,
      mode: payload.mode,
      remoteId: payload.remoteId,
      expiresAt: payload.expiresAt,
      sharing: payload.sharing,
    });
  };

  const submitEdit = async (payload: ListFormPayload) => {
    if (!editingList) return;
    await updateList.mutateAsync({
      id: editingList.id,
      listTypeId: payload.listTypeId,
      name: payload.name,
      description: payload.description,
      context: payload.context,
      mode: payload.mode,
      remoteId: payload.remoteId,
      expiresAt: payload.expiresAt,
      sharing: payload.sharing,
    });
  };

  const canCreate = checkPermission("lists", "create");
  const canUpdate = checkPermission("lists", "update");
  const canDelete = checkPermission("lists", "delete");
  const canUpdateSettings = checkPermission("lists", "update_settings");
  const currentUserDatasetId = sessionQuery.data?.user?.user_dataset_id;
  const isAdmin = sessionQuery.data?.user?.role === "Administrator";

  return (
    <div className="m-5 flex min-h-[calc(100vh-6rem)] flex-col gap-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Lists</h1>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span>{lists.length} visible</span>
            <span>{totalMembers.toLocaleString()} members</span>
            <span>{expiredCount} expired</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canUpdateSettings ? (
            <Button type="button" variant="outline" onClick={() => setTypeManagerOpen(true)}>
              <SettingsIcon className="mr-2 size-4" />
              Manage types
            </Button>
          ) : null}
          {canCreate ? (
            <Button type="button" onClick={() => setCreateOpen(true)}>
              <PlusIcon className="mr-2 size-4" />
              Create list
            </Button>
          ) : null}
        </div>
      </div>

      <div className="grid gap-3 rounded-md border bg-background p-3 sm:grid-cols-2 xl:grid-cols-[minmax(16rem,1fr)_12rem_14rem_auto_auto] xl:items-center">
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search lists..." />
        <Select value={visibilityFilter} onValueChange={(value) => setVisibilityFilter(value as VisibilityFilter)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All visibility</SelectItem>
            <SelectItem value="private">Only me</SelectItem>
            <SelectItem value="users">Shared with people</SelectItem>
            <SelectItem value="dataset">Shared with dataset</SelectItem>
          </SelectContent>
        </Select>
        <Select value={listTypeFilter} onValueChange={setListTypeFilter}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {listTypes.map((listType) => (
              <SelectItem key={listType.id} value={listType.id}>
                {listType.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Switch checked={includeExpired} onCheckedChange={setIncludeExpired} aria-label="Include expired lists" />
          <span>Expired</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Switch checked={includeArchived} onCheckedChange={setIncludeArchived} aria-label="Include archived lists" />
          <span>Archived</span>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden rounded-md border bg-background">
        <Table useDynamicHeight={false}>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[30%]">Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Visibility</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Members</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {listsQuery.isLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="h-32 text-center text-sm text-muted-foreground">
                  Loading lists...
                </TableCell>
              </TableRow>
            ) : listsQuery.isError ? (
              <TableRow>
                <TableCell colSpan={9} className="h-40 text-center text-sm text-destructive">
                  <div className="font-medium">Error loading lists</div>
                  <div className="mt-1 text-xs opacity-80">{listsQuery.error?.message}</div>
                </TableCell>
              </TableRow>
            ) : lists.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="h-40 text-center text-sm text-muted-foreground">
                  No lists match the current filters.
                </TableCell>
              </TableRow>
            ) : (
              lists.map((list) => {
                const isListManager = canManageList(list, currentUserDatasetId, isAdmin);
                const canUpdateList = canUpdate && isListManager;
                const canDeleteList = canDelete && isListManager;
                const hasActions = canUpdateList || canDeleteList;

                return (
                  <TableRow key={list.id}>
                    <TableCell>
                      <Link
                        to="/$datasetSlug/data/lists/$listId"
                        params={{ datasetSlug, listId: list.id }}
                        className="font-medium text-foreground hover:underline"
                      >
                        {list.name}
                      </Link>
                      <div className="text-xs text-muted-foreground">v{list.version}</div>
                    </TableCell>
                    <TableCell>{list.list_type_name}</TableCell>
                    <TableCell>{list.owner_name ?? (list.owner_oauth_client_id ? "API client" : "Unknown")}</TableCell>
                    <TableCell>
                      <VisibilityBadge visibility={list.sharing_mode} />
                    </TableCell>
                    <TableCell>
                      <ListStateBadge list={list} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{list.member_count.toLocaleString()}</TableCell>
                    <TableCell>{formatExpiryDate(list.expires_at)}</TableCell>
                    <TableCell>{formatDateTime(list.updated_at)}</TableCell>
                    <TableCell>
                      {hasActions ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button type="button" variant="ghost" size="icon" className="size-8">
                              <MoreHorizontalIcon className="size-4" />
                              <span className="sr-only">Open list actions</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canUpdateList ? (
                              <DropdownMenuItem
                                onClick={() => setEditingList(list)}
                                disabled={list.status === "archived" || isListExpired(list)}
                              >
                                <EditIcon className="size-4" />
                                Edit
                              </DropdownMenuItem>
                            ) : null}
                            {canUpdateList ? (
                              <DropdownMenuItem onClick={() => setRenewingList(list)}>
                                <RotateCwIcon className="size-4" />
                                Renew
                              </DropdownMenuItem>
                            ) : null}
                            {canUpdateList ? (
                              <DropdownMenuItem
                                onClick={() => setArchivingList(list)}
                                disabled={list.status === "archived"}
                              >
                                <ArchiveIcon className="size-4" />
                                Archive
                              </DropdownMenuItem>
                            ) : null}
                            {canDeleteList ? (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={() => setDeletingList(list)}
                                >
                                  <Trash2Icon className="size-4" />
                                  Delete
                                </DropdownMenuItem>
                              </>
                            ) : null}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <ListFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        listTypes={listTypes}
        isSubmitting={createList.isPending}
        onSubmit={submitCreate}
      />
      <ListFormDialog
        open={!!editingList}
        onOpenChange={(open) => {
          if (!open) setEditingList(null);
        }}
        listTypes={listTypes}
        initialList={editingList}
        isSubmitting={updateList.isPending}
        canManageSharing={!!editingList && isListOwner(editingList, currentUserDatasetId)}
        onSubmit={submitEdit}
      />
      <RenewListDialog
        open={!!renewingList}
        onOpenChange={(open) => {
          if (!open) setRenewingList(null);
        }}
        list={renewingList}
        isSubmitting={renewList.isPending}
        onRenew={async (expiresAt) => {
          if (!renewingList) return;
          await renewList.mutateAsync({ id: renewingList.id, expiresAt });
        }}
      />
      <ConfirmListActionDialog
        open={!!archivingList}
        onOpenChange={(open) => {
          if (!open) setArchivingList(null);
        }}
        title="Archive list"
        description={
          archivingList?.name
            ? `Archive ${archivingList.name}? Archived lists are read-only until restored by an admin.`
            : "Archive this list?"
        }
        confirmLabel="Archive"
        isSubmitting={archiveList.isPending}
        onConfirm={async () => {
          if (!archivingList) return;
          await archiveList.mutateAsync({ id: archivingList.id });
        }}
      />
      <ConfirmListActionDialog
        open={!!deletingList}
        onOpenChange={(open) => {
          if (!open) setDeletingList(null);
        }}
        title="Delete list"
        description={
          deletingList?.name
            ? `Permanently delete ${deletingList.name}? This cannot be undone.`
            : "Permanently delete this list? This cannot be undone."
        }
        confirmLabel="Delete"
        destructive
        isSubmitting={hardDeleteList.isPending}
        onConfirm={async () => {
          if (!deletingList) return;
          await hardDeleteList.mutateAsync({ id: deletingList.id, confirm: true });
        }}
      />
      <ListTypesDialog open={typeManagerOpen} onOpenChange={setTypeManagerOpen} listTypes={listTypes} />
    </div>
  );
}
